// -----------------------------------------------------------------------------
// ui/reactive.ts — the tiny signal store. The ONLY "framework" in the UI.
//
// A signal holds a value and notifies subscribers when it changes. It's used for
// ephemeral UI state (zoom level, status message, export progress) — the editable
// DOCUMENT lives in the ProjectStore, not here. `effect` re-runs a function when
// any of its declared signals change. ~40 lines, no dependencies. (CLAUDE.md §4.)
// -----------------------------------------------------------------------------

export interface Signal<T> {
  get(): T;
  set(value: T): void;
  update(fn: (prev: T) => T): void;
  subscribe(fn: (value: T) => void): () => void;
}

export function signal<T>(initial: T): Signal<T> {
  let value = initial;
  const subscribers = new Set<(value: T) => void>();
  const self: Signal<T> = {
    get: () => value,
    set(next: T) {
      if (Object.is(next, value)) return;
      value = next;
      for (const fn of [...subscribers]) fn(value);
    },
    update(fn: (prev: T) => T) {
      self.set(fn(value));
    },
    subscribe(fn: (value: T) => void) {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },
  };
  return self;
}

// Run `fn` once now and again whenever any dependency signal changes.
export function effect(deps: Array<Signal<unknown>>, fn: () => void): () => void {
  const unsubscribes = deps.map((dep) => dep.subscribe(() => fn()));
  fn();
  return () => unsubscribes.forEach((u) => u());
}
